// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGetArtMutation, useGetArtInspirationsQuery, useAddArtInspirationsMutation, useRemoveArtInspirationMutation } from '../../services/arts.service';
import { Badge, Breadcrumb, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { IArt } from '../../interfaces/arts.interface';
import artTypes from '../../components/ArtTypes';
import { useGenerateSimpleMovieInspirationsMutation } from '../../services/inspiration.service';
import { useGetActivePromptsQuery, PromptType } from '../../services/prompts.service';
import { useBulkImportImdbMoviesMutation } from '../../services/import.service';
import { useGetAllSettingsQuery } from '../../services/settings.service';

import { useAppSelector } from '../../redux/hooks';
import { selectAuthenticatedUser } from '../../redux/slices/auth.slice';
import './ArtPage.scss';

interface MovieInspiration {
    id: number;
    title: string;
    creatorName: string;
    imdbId: string;
    description: string;
    production: string;
    year?: number;
    explainer?: string;
    imageUrl?: string;
}

const ArtPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const [getArt, { data, error, isLoading }] = useGetArtMutation();
    const [showInspirations, setShowInspirations] = useState(false);
    const [inspirations, setInspirations] = useState<MovieInspiration[]>([]);
    const [displayedInspirations, setDisplayedInspirations] = useState<MovieInspiration[]>([]);
    const [generateSimpleMovieInspirations, { isLoading: isGenerating }] = useGenerateSimpleMovieInspirationsMutation();
    const [prompt, setPrompt] = useState<string>('');
    const [selectedSystemPromptUid, setSelectedSystemPromptUid] = useState<string>('');
    const [selectedInspirationPromptUid, setSelectedInspirationPromptUid] = useState<string>('');
    const { data: prompts } = useGetActivePromptsQuery({});
    const { data: settings } = useGetAllSettingsQuery({});
    const { role, access_token } = useAppSelector(selectAuthenticatedUser);
    const [bulkImportMovies] = useBulkImportImdbMoviesMutation();
    const { data: existingInspirations, refetch: refetchInspirations } = useGetArtInspirationsQuery(
        parseInt(id || '0'), 
        { skip: !id }
    );
    const [addArtInspirations] = useAddArtInspirationsMutation();
    const [removeArtInspiration] = useRemoveArtInspirationMutation();
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [isLoadingInspirations, setIsLoadingInspirations] = useState(false);

    const art = data as IArt;
    const chosenArtCategory = artTypes.find(artType => artType.type === art?.category);

    const fetchMovieDataFromOMDB = async (moviesWithExplainers: { imdbId: string; explainer: string }[]) => {
        try {
            const imdbIds = moviesWithExplainers.map(movie => movie.imdbId);
            console.log('Fetching OMDB data for IDs:', imdbIds);
            
            // Filter out duplicates and movies that already exist in current inspirations
            const existingImdbIds = new Set([
                ...inspirations.map(movie => movie.imdbId),
                ...displayedInspirations.map(movie => movie.imdbId)
            ]);
            
            const uniqueMovies = moviesWithExplainers.filter(movie => !existingImdbIds.has(movie.imdbId));
            const uniqueImdbIds = uniqueMovies.map(movie => movie.imdbId);
            
            if (uniqueImdbIds.length === 0) {
                console.log('All movies already exist, skipping import');
                return;
            }
            
            console.log(`Importing ${uniqueImdbIds.length} unique movies out of ${imdbIds.length} requested`);
            
            // First, bulk import the movies to the database
            const importResult = await bulkImportMovies(uniqueImdbIds).unwrap();
            console.log('Bulk import result:', importResult);
            
            // Then, fetch the movie data from our database
            const moviePromises = uniqueMovies.map(async (movieWithExplainer) => {
                try {
                    // Use the existing getArt service to fetch by ID
                    // We need to find the art by imdbId first
                    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
                    const artResponse = await fetch(`${apiUrl}/arts/imdb/${movieWithExplainer.imdbId}`, {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).access_token : ''}`
                        }
                    });
                    
                    if (artResponse.ok) {
                        const artData = await artResponse.json();
                        return {
                            id: artData.id,
                            title: artData.title,
                            creatorName: artData.creatorName,
                            imdbId: artData.imdbId,
                            description: artData.description,
                            production: artData.country,
                            year: artData.year,
                            explainer: movieWithExplainer.explainer,
                            imageUrl: artData.imageUrl
                        };
                    }
                } catch (error) {
                    console.error(`Error fetching data for ${movieWithExplainer.imdbId}:`, error);
                }
                return null;
            });
            
            const movieResults = await Promise.all(moviePromises);
            const validMovies = movieResults.filter(movie => movie !== null) as MovieInspiration[];
            
            // Add to existing inspirations instead of replacing
            setInspirations(prevInspirations => [...prevInspirations, ...validMovies]);
            console.log('Updated inspirations:', validMovies);
            
        } catch (error) {
            console.error('Error fetching OMDB data:', error);
        }
    };

    useEffect(() => {
        if (id && !error) {
            getArt({ id });
        }
    }, [getArt, id, error]);

    useEffect(() => {
        // Check if we should auto-open the inspirations modal
        if (searchParams.get('inspirations') === 'true') {
            setShowInspirations(true);
            // Remove the parameter from URL to clean it up
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.delete('inspirations');
                return newParams;
            });
        }
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        // Clear inspirations when modal is opened
        if (showInspirations) {
            setInspirations([]);
        }
    }, [showInspirations]);
    
    useEffect(() => {
        // Load existing inspirations from database
        if (art?.category === 'movie' && existingInspirations) {
            const formattedInspirations: MovieInspiration[] = (existingInspirations as any).map((inspiration: any) => ({
                id: inspiration.art.id,
                title: inspiration.art.title,
                creatorName: inspiration.art.creatorName,
                imdbId: inspiration.art.imdbId,
                description: inspiration.art.description || 'No description available', // Movie's IMDB description
                production: inspiration.art.country,
                year: inspiration.art.year,
                explainer: inspiration.relationshipDescription, // Relationship explanation
                imageUrl: inspiration.art.imageUrl
            }));
            setDisplayedInspirations(formattedInspirations);
        }
    }, [art, existingInspirations]);

    useEffect(() => {
        // Set default inspiration prompt from settings when component loads
        if (settings && !prompt) {
            const defaultPromptSetting = (settings as any).find((s: any) => s.id === 'default_inspiration_prompt');
            if (defaultPromptSetting) {
                setPrompt(defaultPromptSetting.value);
            }
        }
    }, [settings, prompt]);

    if (isLoading) {
        return (
            <Container className="text-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    if (error || !art) {
        return (
            <Container className="py-4">
                <Card className="text-center border-danger">
                    <Card.Body>
                        <Card.Title>Error</Card.Title>
                        <Card.Text>Failed to load the requested content. Please try again later.</Card.Text>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="art-page py-4">
            <Row className="mb-3">
                <Col>
                    <Breadcrumb className="art-breadcrumb">
                        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
                        <Breadcrumb.Item href="/database">Database</Breadcrumb.Item>
                        <Breadcrumb.Item href={`/arts/${chosenArtCategory?.type}`}>{chosenArtCategory?.title}</Breadcrumb.Item>
                        <Breadcrumb.Item active>{art.title}</Breadcrumb.Item>
                    </Breadcrumb>
                    <hr className="mb-4" />
                </Col>
            </Row>
            
            <Row className="mb-5">
                <Col md={4} className="mb-4 mb-md-0">
                    <div className="art-image-container">
                        <img 
                            src={art.imageUrl} 
                            alt={art.title} 
                            className="img-fluid rounded shadow art-image" 
                        />
                        {art.imdbId && (
                            <a 
                                href={`https://www.imdb.com/title/${art.imdbId}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="imdb-link mt-2 d-inline-block"
                            >
                                View on IMDb
                            </a>
                        )}
                    </div>
                </Col>
                <Col md={8}>
                    <div className="art-details">
                        <h1 className="art-title mb-4">{art.title}</h1>
                        
                        <Row className="mb-4">
                            <Col>
                                {art.genre && art.genre.split(',').map((genre, index) => (
                                    <Badge 
                                        key={index} 
                                        bg="secondary" 
                                        className="me-2 mb-2 genre-badge"
                                    >
                                        {genre.trim()}
                                    </Badge>
                                ))}
                            </Col>
                        </Row>
                        
                        <Card className="info-card mb-4">
                            <Card.Body>
                                <Row>
                                    <Col sm={6} className="mb-3">
                                        <h6 className="text-muted">Creator</h6>
                                        <p className="mb-0">{art.creatorName || 'Unknown'}</p>
                                    </Col>
                                    <Col sm={6} className="mb-3">
                                        <h6 className="text-muted">Year</h6>
                                        <p className="mb-0">{art.year || 'Unknown'}</p>
                                    </Col>
                                    <Col sm={6} className="mb-3">
                                        <h6 className="text-muted">Country</h6>
                                        <p className="mb-0">{art.country || 'Unknown'}</p>
                                    </Col>
                                    <Col sm={6} className="mb-3">
                                        <h6 className="text-muted">Language</h6>
                                        <p className="mb-0">{art.language || 'Unknown'}</p>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                        
                        {art.actors && (
                            <div className="mb-4">
                                <h5 className="section-title">Cast</h5>
                                <p>{art.actors}</p>
                            </div>
                        )}
                        
                        <h5 className="section-title">Description</h5>
                        <p className="art-description">{art.description}</p>
                        
                        {art.category === 'movie' && displayedInspirations.length > 0 && (
                            <div className="inspirations-section mt-4">
                                <h5 className="section-title">Inspirations</h5>
                                <Row className="g-4">
                                    {displayedInspirations.map((inspiration) => (
                                        <Col md={4} key={inspiration.id}>
                                            <Card className="inspiration-card h-100">
                                                <div className="inspiration-image-container">
                                                    <Card.Img 
                                                        variant="top" 
                                                        src={inspiration.imageUrl} 
                                                        alt={inspiration.title} 
                                                        className="inspiration-image"
                                                    />
                                                </div>
                                                <Card.Body>
                                                    <Card.Title className="inspiration-title">
                                                        <a href={`/arts/view/${inspiration.id}`}>
                                                            {inspiration.title}
                                                        </a>
                                                    </Card.Title>
                                                    <Card.Subtitle className="mb-2 text-muted">
                                                        {inspiration.creatorName} ({inspiration.production})
                                                    </Card.Subtitle>
                                                    <Card.Text className="inspiration-description">
                                                        {inspiration.explainer && inspiration.explainer !== 'Generated inspiration' && inspiration.explainer !== 'Inspiration' ? (
                                                            <>
                                                                <small className="text-muted d-block mb-1"><strong>Relation:</strong></small>
                                                                {inspiration.explainer}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <small className="text-muted d-block mb-1"><strong>Description:</strong></small>
                                                                {inspiration.description}
                                                            </>
                                                        )}
                                                    </Card.Text>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}
                        
                        {art.category === 'movie' && access_token && (role === 'admin' || role === 'manager') && (
                            <>
                                <Button 
                                    variant="outline-primary" 
                                    className="mt-3 me-2"
                                    onClick={() => setShowInspirations(true)}
                                >
                                    Add inspirations
                                </Button>
                                {displayedInspirations.length > 0 && (
                                    <Button 
                                        variant="outline-danger" 
                                        className="mt-3"
                                        onClick={() => setShowRemoveModal(true)}
                                    >
                                        Remove inspirations
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Inspirations Modal */}
            <Modal 
                show={showInspirations} 
                onHide={() => setShowInspirations(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Movie Inspirations</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* Text Generation Section */}
                    <div className="generation-section mb-4 p-3 border rounded">
                        <h5>Generate Inspiration Ideas</h5>
                        <Form>
                            {access_token && (role === 'manager' || role === 'admin') && prompts && prompts.length > 0 && (
                                <>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Select System Prompt (Rules & Role)</Form.Label>
                                        <Form.Select
                                            value={selectedSystemPromptUid}
                                            onChange={(e) => setSelectedSystemPromptUid(e.target.value)}
                                        >
                                            <option value="">Choose a system prompt...</option>
                                            {prompts.filter((p: any) => p.promptType === PromptType.PROMPT_SYSTEM).map((prompt: any) => (
                                                <option key={prompt.id} value={prompt.id}>{prompt.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    
                                    <Form.Group className="mb-3">
                                        <Form.Label>Select Inspiration Prompt (Request)</Form.Label>
                                        <Form.Select
                                            value={selectedInspirationPromptUid}
                                            onChange={(e) => {
                                                const selectedId = e.target.value;
                                                setSelectedInspirationPromptUid(selectedId);
                                                if (selectedId) {
                                                    const selectedPrompt = prompts.find((p: any) => p.id.toString() === selectedId);
                                                    if (selectedPrompt) {
                                                        setPrompt(selectedPrompt.content);
                                                    }
                                                } else {
                                                    // Reset to default inspiration prompt when none selected
                                                    const defaultPromptSetting = (settings as any)?.find((s: any) => s.id === 'default_inspiration_prompt');
                                                    setPrompt(defaultPromptSetting?.value || '');
                                                }
                                            }}
                                        >
                                            <option value="">Choose an inspiration prompt...</option>
                                            {prompts.filter((p: any) => p.promptType === PromptType.PROMPT_INSPIRATION).map((prompt: any) => (
                                                <option key={prompt.id} value={prompt.id}>{prompt.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </>
                            )}
                            <Form.Group className="mb-3">
                                <Form.Label>Enter a prompt to generate inspiration ideas</Form.Label>
                                <div className="mb-2">
                                    <small className="text-muted">
                                        <strong>Sample prompts with placeholders:</strong><br/>
                                        • "Give me 5 movies similar to <code>{'{movieName}'}</code> with similar themes and visual style"<br/>
                                        • "Find 3 movies that influenced <code>{'{movieName}'}</code>'s cinematography and narrative structure"<br/>
                                        • "Recommend films from the same era as <code>{'{movieName}'}</code> (IMDb: <code>{'{imdbId}'}</code>) with comparable storytelling techniques"<br/>
                                        • "List movies that share thematic elements with <code>{'{movieName}'}</code> - provide brief explanations"
                                    </small>
                                </div>
                                <Form.Control 
                                    as="textarea" 
                                    rows={2} 
                                    placeholder="E.g., Give me 5 movies similar to {movieName} with themes of family and power" 
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                />
                            </Form.Group>
                            <Button 
                                variant="primary" 
                                onClick={async () => {
                                    if (prompt.trim()) {
                                        try {
                                            // Reset inspirations list when generate is clicked
                                            setInspirations([]);
                                            setIsLoadingInspirations(true);
                                            
                                            // Get selected prompts
                                            const systemPromptData = selectedSystemPromptUid ? prompts?.find((p: any) => p.id.toString() === selectedSystemPromptUid) : null;
                
                                            
                                            // Default system prompt from settings if none selected
                                            const defaultSystemPromptSetting = (settings as any)?.find((s: any) => s.id === 'default_inspiration_system_prompt');
                                            let systemPrompt = defaultSystemPromptSetting?.value || "You are a film expert. Provide concise, insightful suggestions for movie inspirations. Focus on thematic connections, visual style, and narrative techniques.";
                                            
                                            // Use selected system prompt if available
                                            if (systemPromptData) {
                                                systemPrompt = systemPromptData.content;
                                            }
                                            
                                            // Prepare user prompt with placeholder replacement
                                            let userPrompt = prompt;
                                            
                                            // Replace placeholders with actual movie data
                                            if (art) {
                                                userPrompt = userPrompt
                                                    .replace(/\{movieName\}/g, art.title || 'Unknown Movie')
                                                    .replace(/\{imdbId\}/g, art.imdbId || 'N/A');
                                            }
                                            
                                            const response = await generateSimpleMovieInspirations({
                                                prompt: userPrompt,
                                                systemPrompt: systemPrompt,
                                                temperature: 0.7
                                            }).unwrap();
                                            
                                            // Extract movies with explainers and fetch OMDB data
                                            if (response.movies && Array.isArray(response.movies)) {
                                                await fetchMovieDataFromOMDB(response.movies);
                                            }
                                            
                                            setIsLoadingInspirations(false);
                                        } catch (error) {
                                            console.error('Error generating text:', error);
                                            setIsLoadingInspirations(false);
                                        }
                                    }
                                }}
                                disabled={isLoadingInspirations || !prompt.trim()}
                            >
                                {isLoadingInspirations ? (
                                    <>
                                        <Spinner 
                                            as="span" 
                                            animation="border" 
                                            size="sm" 
                                            role="status" 
                                            aria-hidden="true" 
                                            className="me-2"
                                        />
                                        Generating inspirations...
                                    </>
                                ) : 'Generate'}
                            </Button>
                        </Form>
                        
                    </div>
                    
                    {isLoadingInspirations && (
                        <div className="text-center py-4">
                            <Spinner animation="border" role="status" className="me-2">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <div className="mt-2">
                                <strong>
                                    {isGenerating ? 'Generating movie inspirations...' : 'Loading movie details...'}
                                </strong>
                                <div className="text-muted small">
                                    {isGenerating 
                                        ? 'Please wait while we find relevant movies and their relation explanations'
                                        : 'Fetching movie information from IMDB database'
                                    }
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {!isLoadingInspirations && inspirations.length > 0 ? (
                        <>
                            <p className="mb-4">Found these inspirations:</p>
                            
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>IMDb ID</th>
                                        <th>Title</th>
                                        <th>Creator</th>
                                        <th>Relation</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {inspirations.map((movie) => {
                                        const alreadyExists = displayedInspirations.some(existing => existing.imdbId === movie.imdbId);
                                        return (
                                            <tr key={movie.id} className={alreadyExists ? 'table-warning' : ''}>
                                                <td>
                                                    <a 
                                                        href={`https://www.imdb.com/title/${movie.imdbId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {movie.imdbId}
                                                    </a>
                                                    {alreadyExists && <Badge bg="warning" className="ms-2">Already Added</Badge>}
                                                </td>
                                                <td>{movie.title}{movie.year ? ` (${movie.year})` : ''}</td>
                                                <td>{movie.creatorName}</td>
                                                <td>{movie.explainer || movie.description}</td>
                                                <td>
                                        <Button 
                                            variant="danger" 
                                            size="sm" 
                                            className="me-1"
                                            onClick={() => {
                                                setInspirations(inspirations.filter(item => item.id !== movie.id));
                                            }}
                                        >
                                            Remove
                                        </Button>
                                         <Button 
                                             variant="warning" 
                                             size="sm" 
                                             className="me-1"
                                             disabled={alreadyExists}
                                             onClick={async () => {
                                                 try {
                                                     // Check if this movie is already in displayed inspirations (existing relationships)
                                                     const alreadyExists = displayedInspirations.some(existing => existing.imdbId === movie.imdbId);
                                                     if (alreadyExists) {
                                                         console.log(`${movie.title} already exists as an inspiration`);
                                                         return;
                                                     }
                                                     
                                                     // First import the movie to ensure it exists in database
                                                     await bulkImportMovies([movie.imdbId]).unwrap();
                                                     console.log(`Successfully imported ${movie.title}`);
                                                     
                                                     // Then add the inspiration relationship
                                                     if (art && art.id) {
                                                         await addArtInspirations({
                                                             artId: parseInt(art.id),
                                                             inspirationArtIds: [movie.id],
                                                             relationshipDescription: movie.explainer || 'Generated inspiration'
                                                         }).unwrap();
                                                         console.log(`Added inspiration relationship for ${movie.title}`);
                                                         
                                                         // Remove the movie from the inspirations list after successful import
                                                         setInspirations(prevInspirations => 
                                                             prevInspirations.filter(item => item.id !== movie.id)
                                                         );
                                                         
                                                         refetchInspirations();
                                                     }
                                                 } catch (error) {
                                                     console.error(`Error importing ${movie.title}:`, error);
                                                 }
                                             }}
                                         >
                                             {alreadyExists ? 'Already Added' : 'Import'}
                                         </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                        </tbody>
                    </Table>
                        </>
                    ) : !isLoadingInspirations && (
                        <div className="text-center py-4">
                            <p className="text-muted">No inspirations found yet. Generate some inspiration ideas above to populate this table.</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="primary" 
                        className="me-auto"
                        onClick={async () => {
                            if (inspirations.length > 0 && art && art.id) {
                                try {
                                    // Filter out movies that already exist as inspirations
                                    const existingImdbIds = new Set(displayedInspirations.map(existing => existing.imdbId));
                                    const newInspirations = inspirations.filter(movie => !existingImdbIds.has(movie.imdbId));
                                    
                                    if (newInspirations.length === 0) {
                                        console.log('All selected movies already exist as inspirations');
                                        return;
                                    }
                                    
                                    // Import and add each inspiration individually to preserve explainers
                                    for (const movie of newInspirations) {
                                        try {
                                            // First import the movie to ensure it exists in database
                                            await bulkImportMovies([movie.imdbId]).unwrap();
                                            console.log(`Successfully imported ${movie.title}`);
                                            
                                            // Then add the inspiration relationship with the explainer
                                            await addArtInspirations({
                                                artId: parseInt(art.id),
                                                inspirationArtIds: [movie.id],
                                                relationshipDescription: movie.explainer || 'Generated inspiration'
                                            }).unwrap();
                                            console.log(`Added inspiration relationship for ${movie.title} with explainer: ${movie.explainer}`);
                                        } catch (error) {
                                            console.error(`Error importing ${movie.title}:`, error);
                                        }
                                    }
                                    
                                    console.log(`Successfully added ${newInspirations.length} inspiration relationships`);
                                    
                                    // Clear the inspirations list after successful import
                                    setInspirations([]);
                                    
                                    refetchInspirations();
                                    setShowInspirations(false);
                                } catch (error) {
                                    console.error('Error importing inspirations:', error);
                                }
                            }
                        }}
                        disabled={inspirations.length === 0}
                    >
                        Submit All ({inspirations.length})
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Remove Inspirations Modal */}
            <Modal 
                show={showRemoveModal} 
                onHide={() => setShowRemoveModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Remove Movie Inspirations</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {displayedInspirations.length > 0 ? (
                        <>
                            <p className="mb-4">Select inspirations to remove:</p>
                            
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Creator</th>
                                        <th>Relation</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedInspirations.map((inspiration) => (
                                        <tr key={inspiration.id}>
                                            <td>{inspiration.title}{inspiration.year ? ` (${inspiration.year})` : ''}</td>
                                            <td>{inspiration.creatorName}</td>
                                            <td>{inspiration.explainer || inspiration.description}</td>
                                            <td>
                                                <Button 
                                                    variant="danger" 
                                                    size="sm" 
                                                    onClick={async () => {
                                                        try {
                                                            if (art && art.id) {
                                                                // Find the inspiration relationship ID
                                                                const inspirationRelationship = (existingInspirations as any)?.find(
                                                                    (existing: any) => existing.art.id === inspiration.id
                                                                );
                                                                
                                                                if (inspirationRelationship) {
                                                                    await removeArtInspiration({
                                                                        artId: parseInt(art.id),
                                                                        inspirationId: inspirationRelationship.id
                                                                    }).unwrap();
                                                                    
                                                                    console.log(`Removed inspiration: ${inspiration.title}`);
                                                                    refetchInspirations();
                                                                }
                                                            }
                                                        } catch (error) {
                                                            console.error(`Error removing inspiration ${inspiration.title}:`, error);
                                                        }
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    ) : (
                        <div className="text-center py-4">
                            <p className="text-muted">No inspirations to remove.</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

export default ArtPage;